import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  isNotesChannel,
  resolveDisplayName,
  type ChannelWithMembership,
  type NameDisplayMode,
  type NotifyLevel,
  type PublicUser,
} from '@avslack/shared';
import {
  addChannelMembers,
  createChannel,
  joinChannel,
  leaveChannel,
  listChannelMembers,
  listChannels,
  markChannelRead,
  markChannelUnread,
  openDm,
  removeChannelMember,
  reorderChannels,
  updateChannel,
  updateMembership,
} from '../lib/api';
import { holdUnread } from '../lib/unread-hold';
import { withOfflineFallback } from '../lib/offline-cache';

export const channelsKey = ['channels'] as const;

export function useChannels() {
  return useQuery({
    queryKey: channelsKey,
    // Terero nelkul az utolso ismert lista - lasd lib/offline-cache.ts.
    queryFn: withOfflineFallback('channels', listChannels),
    staleTime: 30_000,
  });
}

export function useChannelMembers(channelId: string | null) {
  return useQuery({
    queryKey: ['channel-members', channelId],
    queryFn: () => listChannelMembers(channelId!),
    enabled: Boolean(channelId),
    staleTime: 30_000,
  });
}

/** A csatorna tagsagat modosito muveletek - a lista es a csatorna is frissul. */
export function useChannelMemberActions(channelId: string) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['channel-members', channelId] });
    void queryClient.invalidateQueries({ queryKey: channelsKey });
  };

  const add = useMutation({
    mutationFn: (userIds: string[]) => addChannelMembers(channelId, userIds),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (userId: string) => removeChannelMember(channelId, userId),
    onSuccess: invalidate,
  });

  return { add, remove };
}

// ─── Cache-frissitesek WebSocket-esemenyekre ──────────────────────────────────

export function upsertChannel(queryClient: QueryClient, channel: ChannelWithMembership): void {
  queryClient.setQueryData<ChannelWithMembership[]>(channelsKey, (current) => {
    if (!current) return [channel];
    const index = current.findIndex((c) => c.id === channel.id);
    if (index === -1) return [...current, channel];
    const next = [...current];
    // A szerver-esemeny a mervado, de a sajat beallitasokat nem irjuk felul
    // egy olyan esemennyel, ami nem rolunk szol.
    next[index] = { ...next[index]!, ...channel };
    return next;
  });
}

export function removeChannel(queryClient: QueryClient, channelId: string): void {
  queryClient.setQueryData<ChannelWithMembership[]>(channelsKey, (current) =>
    (current ?? []).filter((c) => c.id !== channelId),
  );
}

/**
 * Uj uzenet: no az olvasatlan szam es elore kerul a csatorna a listaban.
 *
 * A CSENDES sor (telefonos hivasnaplo, `isQuietMessage`) nem noveli az
 * olvasatlan szamot - a szerver is levonja (channels/store `loadQuietUnread`).
 * Ha kozben uzenet maradt ki (hezag), azokat olvasatlannak vesszuk; a hezag
 * ugyis ujratoltest indit, ami a pontos szamot hozza.
 */
export function bumpChannel(
  queryClient: QueryClient,
  channelId: string,
  seq: number,
  createdAt: string,
  quiet = false,
): void {
  queryClient.setQueryData<ChannelWithMembership[]>(channelsKey, (current) =>
    (current ?? []).map((channel) => {
      if (channel.id !== channelId) return channel;
      const lastSeq = Math.max(channel.lastSeq, seq);
      const added = Math.max(
        0,
        lastSeq - Math.max(channel.lastSeq, channel.membership.lastReadSeq),
      );
      return {
        ...channel,
        lastSeq,
        lastMessageAt: createdAt,
        unreadCount: Math.min(
          Math.max(0, lastSeq - channel.membership.lastReadSeq),
          channel.unreadCount + (quiet ? Math.max(0, added - 1) : added),
        ),
      };
    }),
  );
}

/** Olvasottsag valtozott (barmelyik fulon) - itt tunik el az olvasatlan jelzes. */
export function applyReadUpdate(
  queryClient: QueryClient,
  channelId: string,
  lastReadSeq: number,
): void {
  const before = queryClient
    .getQueryData<ChannelWithMembership[]>(channelsKey)
    ?.find((channel) => channel.id === channelId);
  queryClient.setQueryData<ChannelWithMembership[]>(channelsKey, (current) =>
    (current ?? []).map((channel) =>
      channel.id === channelId
        ? {
            ...channel,
            membership: { ...channel.membership, lastReadSeq },
            /*
              Elore olvasasnal a szam csak csokkenhet; a sorszam-kulonbseg felso
              korlat, mert a csendes sorok (telefonos hivasnaplo) nem szamitanak.
              Visszafele ("olvasatlanna jelolom") a pontos szamot a szerver adja
              (lasd a hivo oldalt) - addig a kulonbseg.
            */
            unreadCount:
              lastReadSeq >= channel.membership.lastReadSeq
                ? Math.min(channel.unreadCount, Math.max(0, channel.lastSeq - lastReadSeq))
                : Math.max(0, channel.lastSeq - lastReadSeq),
            /*
              A NEM FOGADOTT HIVAS jelzese is elmulik az olvasassal.

              A szerver ugyanezt mondja majd a kovetkezo lekerdezesnel, de
              addig a jeloles ott maradna egy beszelgetesen, amit epp
              vegigolvastunk - es ez pont az a "rosszul jelez" tunet, amit
              kerulunk. Csak akkor toroljuk, ha a beszelgetes VEGIG el van
              olvasva: reszleges olvasasnal nem tudhatjuk, a hivas-sor a
              hatarvonal melyik oldalan van.
            */
            ...(lastReadSeq >= channel.lastSeq ? { hasMissedCall: false } : {}),
          }
        : channel,
    ),
  );
  // Olvasatlanna jeloles: a csendes sorokat csak a szerver tudja levonni.
  if (before && lastReadSeq < before.membership.lastReadSeq) {
    void queryClient.invalidateQueries({ queryKey: channelsKey });
  }
}

export function applyMemberCount(
  queryClient: QueryClient,
  channelId: string,
  memberCount: number,
): void {
  queryClient.setQueryData<ChannelWithMembership[]>(channelsKey, (current) =>
    (current ?? []).map((c) => (c.id === channelId ? { ...c, memberCount } : c)),
  );
}

// ─── Muveletek ────────────────────────────────────────────────────────────────

export function useCreateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createChannel,
    onSuccess: (channel) => upsertChannel(queryClient, channel),
  });
}

export function useJoinChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: joinChannel,
    onSuccess: (channel) => upsertChannel(queryClient, channel),
  });
}

export function useLeaveChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: leaveChannel,
    onSuccess: (_data, channelId) => removeChannel(queryClient, channelId),
  });
}

export function useOpenDm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: openDm,
    onSuccess: (result) => upsertChannel(queryClient, result.channel),
  });
}

/**
 * A csatorna beallitasai.
 *
 * A LATHATOSAG is itt valtozik. Az nem apro beallitas: nyilvanossa teve a
 * teljes elozmeny lathatova valik tagsag nelkul is - a szerver ezert
 * rendszeruzenetet is ir rola a csatornaba.
 */
export function useUpdateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      patch,
    }: {
      channelId: string;
      patch: {
        name?: string;
        topic?: string | null;
        purpose?: string | null;
        isArchived?: boolean;
        kind?: 'public' | 'private';
        postPolicy?: 'all' | 'admins';
      };
    }) => updateChannel(channelId, patch).then((r) => r.channel),
    onSuccess: (channel) => upsertChannel(queryClient, channel),
  });
}

export function useToggleMembershipFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      patch,
    }: {
      channelId: string;
      patch: {
        isStarred?: boolean;
        isMuted?: boolean;
        isHidden?: boolean;
        notifyLevel?: NotifyLevel;
      };
    }) => updateMembership(channelId, patch),
    onSuccess: (channel) => upsertChannel(queryClient, channel),
  });
}

/**
 * Az oldalsav SAJAT sorrendje - huzas utan.
 *
 * Azonnal atrendezzuk a gyorstarat, es csak utana kerdezzuk a szervert: a
 * huzas erzese kulonben akadna egy halozati kor erejeig. Ha a mentes elbukik,
 * visszaallitjuk az eredetit - nem hagyunk a felhasznalon egy olyan sorrendet,
 * ami a kovetkezo betoltesnel ugyis eltunne.
 */
export function useReorderChannels() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelIds: string[]) => reorderChannels(channelIds),
    onMutate: async (channelIds) => {
      await queryClient.cancelQueries({ queryKey: channelsKey });
      const previous = queryClient.getQueryData<ChannelWithMembership[]>(channelsKey);

      // 1-tol szamozunk: a 0 marad a "meg nem rendezte at" jelentesenek.
      const rank = new Map(channelIds.map((id, index) => [id, index + 1]));
      queryClient.setQueryData<ChannelWithMembership[]>(channelsKey, (current) =>
        (current ?? []).map((channel) => {
          const sortOrder = rank.get(channel.id);
          return sortOrder === undefined
            ? channel
            : { ...channel, membership: { ...channel.membership, sortOrder } };
        }),
      );

      return { previous };
    },
    onError: (_error, _ids, context) => {
      if (context?.previous) queryClient.setQueryData(channelsKey, context.previous);
    },
  });
}

/**
 * Olvasottsag jelolese.
 *
 * A jelzes AZONNAL eltunik, nem a valasz megerkezesekor.
 *
 * Korabban a szamlalo a szerver koruthossza utan frissult: rossz halozaton ez
 * masodperceket jelentett, es kozben az oldalsavban tovabb vilagitott az
 * olvasatlan jeloles egy olyan csatornan, amit epp nezett az ember. A
 * muveletnek nincs ertelmes kudarca sem - hiba eseten visszaallitjuk az
 * elozo allast, es a kovetkezo probalkozas ujra nekifut.
 */
/**
 * Folyamatban levo olvasottnak jeloles csatornankent.
 *
 * Az olvatlanna jeloles MEGVARJA: a menupontra kattintas egy koppintas is az
 * uzeneten, ami elobb olvasottnak jelol (lasd ChannelView `handleSeen`). Ha a
 * ket keres sorrendje a halozaton felcserelodne, az olvasottnak jeloles
 * felulirna az olvatlant.
 */
const inflightReads = new Map<string, Promise<unknown>>();

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, seq }: { channelId: string; seq: number }) => {
      const request = markChannelRead(channelId, seq);
      inflightReads.set(channelId, request);
      void request
        .catch(() => undefined)
        .then(() => {
          if (inflightReads.get(channelId) === request) inflightReads.delete(channelId);
        });
      return request;
    },
    onMutate: ({ channelId, seq }) => {
      const channel = queryClient
        .getQueryData<ChannelWithMembership[]>(channelsKey)
        ?.find((c) => c.id === channelId);
      const previous = channel?.membership.lastReadSeq ?? 0;

      // Csak NOHET - ugyanaz a szabaly, mint a szerveren.
      if (seq > previous) applyReadUpdate(queryClient, channelId, seq);

      return { previous };
    },
    onError: (_error, variables, context) => {
      if (context) applyReadUpdate(queryClient, variables.channelId, context.previous);
    },
    onSuccess: (result, variables) =>
      applyReadUpdate(queryClient, variables.channelId, result.lastReadSeq),
  });
}

/**
 * OLVATLANNA JELOLES egy uzenettol.
 *
 * Elobb a visszatartas (hogy a nezet ne jelolje azonnal vissza), aztan
 * megvarjuk a folyamatban levo olvasottnak jelolest, es csak utana kerjuk az
 * olvatlant - igy a ketto nem elozheti meg egymast.
 */
export function useMarkUnread() {
  const queryClient = useQueryClient();
  return useCallback(
    async (channelId: string, seq: number) => {
      holdUnread(channelId);
      await inflightReads.get(channelId)?.catch(() => undefined);
      const result = await markChannelUnread(channelId, seq);
      applyReadUpdate(queryClient, channelId, result.lastReadSeq);
    },
    [queryClient],
  );
}

// ─── Megjelenites ─────────────────────────────────────────────────────────────

/** DM-nel a masik fel neve, egyebkent a csatornanev. */
export function channelTitle(
  channel: ChannelWithMembership,
  usersById: Map<string, PublicUser>,
  meId: string,
  /** A nézői beállítás: rendes nevek vagy becenevek (lásd `useUsers().nameDisplay`). */
  mode: NameDisplayMode,
): string {
  if (channel.kind === 'public' || channel.kind === 'private') return channel.name ?? 'csatorna';

  if (isNotesChannel(channel, meId)) return 'Saját jegyzetek';

  /*
    Az ures nevsor NEM jelenti azt, hogy jegyzet.

    Egy DM akkor is elfogyhat, ha a masik fel fioktja megszunt. Az ilyen
    beszelgetes korabban "Saját jegyzetek" nevvel jelent meg - vagyis a
    sajat jegyzetek koze keveredett, ugyanazzal a cimkevel, tobbszor.
  */
  const others = (channel.memberIds ?? []).filter((id) => id !== meId);
  if (others.length === 0) return 'Törölt beszélgetés';

  const names = others.map((id) => {
    const user = usersById.get(id);
    return user ? resolveDisplayName(user, mode) : 'Ismeretlen';
  });
  return names.length <= 3
    ? names.join(', ')
    : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
}

export function channelPrefix(channel: ChannelWithMembership): string {
  if (channel.kind === 'public') return '#';
  if (channel.kind === 'private') return '🔒';
  return '';
}

/**
 * Rendezes.
 *
 * Harom szint, ebben a sorrendben:
 *
 *  1. Amit a felhasznalo KEZZEL a helyere huzott (`sortOrder > 0`). Ez a
 *     legerosebb jel: ha valaki egyszer odatette, ott is marad - nem lokja
 *     lejjebb egy uj uzenet valahol maskor.
 *  2. Csillagozott.
 *  3. Legutobbi uzenet, vegul nev.
 *
 * A kezzel rendezettek azert kerulnek a tobbi ele, mert kulonben egy friss
 * uzenet atugrana rajtuk, es ertelmet vesztene a huzas.
 */
/**
 * Latszik-e az OLDALSAVBAN.
 *
 * Az archivalt csatorna es az, amit a felhasznalo maga rejtett el (Beallitasok
 * > Csatornak), kimarad - es az olvasatlan-jelzesekbe sem szamit, hiszen nincs
 * hol "elolvasni". Mindketto elerheto marad a Beallitasokbol es a
 * gyorskeresobol.
 */
export function isListedChannel(channel: ChannelWithMembership): boolean {
  return !channel.isArchived && !channel.membership.isHidden;
}

export function sortChannels(channels: ChannelWithMembership[]): ChannelWithMembership[] {
  return [...channels].sort((a, b) => {
    const aOrder = a.membership.sortOrder;
    const bOrder = b.membership.sortOrder;
    if (aOrder > 0 !== bOrder > 0) return aOrder > 0 ? -1 : 1;
    if (aOrder > 0 && aOrder !== bOrder) return aOrder - bOrder;

    if (a.membership.isStarred !== b.membership.isStarred) return a.membership.isStarred ? -1 : 1;
    const aTime = a.lastMessageAt ?? '';
    const bTime = b.lastMessageAt ?? '';
    if (aTime !== bTime) return bTime.localeCompare(aTime);
    return (a.name ?? '').localeCompare(b.name ?? '', 'hu');
  });
}
